import { array, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { FieldArray, formValueSelector, initialize, reduxForm } from 'redux-form';
import { Company, FinancialDocumentPdfTemplateType, FinancialDocumentType } from '@mews-commander/data-access';
import { ActionType, TextKey } from '@mews-commander/enums';
import { Option } from '@mews-commander/legacy-data-structures';
import { sendEmails, sendInvoiceReminder } from '@mews-commander/legacy-entities/src/actions/typed/bills';
import { getLanguageOptions } from '@mews-commander/legacy-entities/src/ducks/languages';
import {
    CheckboxGroupField,
    eitherArrayNotEmpty,
    FinancialDocumentTemplateSelectField,
    InputField,
    optionalEmail,
    presence,
    SelectField,
    validation,
} from '@mews-commander/legacy-form';
import { ApplicationState, CommanderThunkDispatch, Customer, Option as OptionType } from '@mews-commander/types';
import { createOption, OverlayedLoader } from '@mews-commander/ui';
import { compactArray } from '@mews/fp';
import { LocalizedText, lt } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import { closeDialog, openDialog } from '../../../../../modules/dialog/reducer';
import { SendEmailType } from '../Action/SendFinancialDocumentToEmailAction';
import { formName as repeatedPrintForm, WithOptionalFirstUsedTemplateType } from '../RepeatedPrintDialogForm';
import { REMINDER_DIALOG_NAME } from '../SendReminderToEmailDialog/constants';
import { FORM_NAME } from './constants';
import { useGetBillCreditNote } from './queries';
import SendEmailDialogCheckboxLabel from './SendEmailDialogCheckboxLabel';
import SendDialogEmailFields from './SendEmailDialogFields';

const fieldNames = {
    language: 'language',
    defaultEmails: 'defaultEmails',
    emails: 'emails',
    printReason: 'printReason',
    linkedBillIds: 'linkedBillIds',
} as const;

export interface FormValues extends WithOptionalFirstUsedTemplateType {
    defaultEmails: string[];
    language: string;
    type?: ActionType.SendFinancialDocumentToEmail;
    emails: { value: string }[];
    financialDocumentId: string;
    documentType: FinancialDocumentType;
    printReason?: string;
    showPrintReason: boolean;
    setShowPrintReason: (show: boolean) => void;
    template: FinancialDocumentPdfTemplateType;
    requirePrintReasonOnTemplateChange?: boolean;
    linkedBillIds?: string[];
}

interface DefaultEmailsPropsOptions {
    ownerEmail?: Customer['email'];
    companyEmail?: Company['email'];
    isOwnerCompany: boolean;
}

interface StateProps extends DefaultEmailsPropsOptions {
    languageOptions: OptionType[];
    showPrintReason: boolean;
    allowedTemplateTypes?: FinancialDocumentPdfTemplateType[];
    financialDocumentId: string;
    documentType: FinancialDocumentType;
    type: SendEmailType;
}

interface DispatchProps {
    onSubmit: (values: FormValues) => void;
    onSubmitSuccess: () => void;
}

type SendEmailDialogFormProps = StateProps & DispatchProps;

const defaultEmailsProps = ({ ownerEmail, companyEmail, isOwnerCompany }: DefaultEmailsPropsOptions) => ({
    label: <LocalizedText textKey={TextKey.Recipients} />,
    name: fieldNames.defaultEmails,
    options: compactArray([
        companyEmail
            ? Option({
                  name: fieldNames.defaultEmails,
                  value: companyEmail,
                  label: <SendEmailDialogCheckboxLabel data-company-email label={companyEmail} tag={TextKey.Company} />,
              })
            : null,
        ownerEmail
            ? Option({
                  name: fieldNames.defaultEmails,
                  value: ownerEmail,
                  label: (
                      <SendEmailDialogCheckboxLabel
                          data-customer-email
                          label={ownerEmail}
                          tag={isOwnerCompany ? TextKey.Company : TextKey.Customer}
                      />
                  ),
              })
            : null,
    ]),
});

const SendEmailDialogForm = ({
    languageOptions,
    ownerEmail,
    companyEmail,
    documentType,
    showPrintReason,
    isOwnerCompany,
    allowedTemplateTypes,
    financialDocumentId,
    type,
}: SendEmailDialogFormProps) => {
    const creditNoteResult = useGetBillCreditNote(financialDocumentId, documentType);
    return (
        <Stack vertical spacing="200" data-send-email-dialog-form>
            <OverlayedLoader show={creditNoteResult.isLoading} />
            {!!allowedTemplateTypes && (
                <FinancialDocumentTemplateSelectField
                    helper={type === 'SendReminder' ? <LocalizedText textKey={TextKey.BillFormatHelper} /> : null}
                    allowedTemplateTypes={allowedTemplateTypes}
                    size="medium"
                />
            )}
            <SelectField
                name={fieldNames.language}
                label={<LocalizedText textKey={TextKey.Language} />}
                options={languageOptions}
                size="medium"
            />
            <CheckboxGroupField
                {...defaultEmailsProps({
                    ownerEmail,
                    companyEmail,
                    isOwnerCompany,
                })}
            />
            <FieldArray name={fieldNames.emails} component={SendDialogEmailFields} />
            {creditNoteResult.data ? (
                <CheckboxGroupField
                    label={lt('IncludeLinkedDocuments')}
                    name={fieldNames.linkedBillIds}
                    options={[
                        createOption({
                            value: creditNoteResult.data.id,
                            label: creditNoteResult.data.name,
                        }),
                    ]}
                />
            ) : null}
            {showPrintReason ? (
                <InputField name={fieldNames.printReason} label={<LocalizedText textKey={TextKey.PrintReason} />} />
            ) : null}
        </Stack>
    );
};

const getConstraints = ({ showPrintReason }: SendEmailDialogFormProps) => ({
    [fieldNames.defaultEmails]: {
        eitherArrayNotEmpty: eitherArrayNotEmpty({
            otherFieldName: fieldNames.emails,
            message: TextKey.NoEmailsProvided,
        }),
    },
    [fieldNames.emails]: {
        fieldArray: {
            value: { optionalEmail },
        },
        eitherArrayNotEmpty: eitherArrayNotEmpty({
            otherFieldName: fieldNames.defaultEmails,
            message: TextKey.NoEmailsProvided,
        }),
    },
    [fieldNames.printReason]: {
        ...(showPrintReason ? { presence } : {}),
    },
});

export default pipe(
    SendEmailDialogForm,
    reduxForm({
        form: FORM_NAME,
        validate: validation(getConstraints),
    }),
    connect<StateProps, DispatchProps, { type: SendEmailType }, ApplicationState>(
        (state) => ({
            languageOptions: getLanguageOptions(state),
            ownerEmail: formValueSelector(FORM_NAME)(state, 'ownerEmail'),
            isOwnerCompany: formValueSelector(FORM_NAME)(state, 'isOwnerCompany'),
            companyEmail: formValueSelector(FORM_NAME)(state, 'companyEmail'),
            showPrintReason: formValueSelector(FORM_NAME)(state, 'showPrintReason'),
            allowedTemplateTypes: formValueSelector(FORM_NAME)(state, 'allowedTemplateTypes'),
            financialDocumentId: formValueSelector(FORM_NAME)(state, 'financialDocumentId'),
            documentType: formValueSelector(FORM_NAME)(state, 'documentType'),
            type: formValueSelector(FORM_NAME)(state, 'type'),
        }),
        (dispatch: CommanderThunkDispatch, { type }) => ({
            onSubmit: async (values) => {
                const {
                    financialDocumentId,
                    documentType,
                    language,
                    printReason,
                    setShowPrintReason,
                    template,
                    requirePrintReasonOnTemplateChange,
                    firstUsedTemplateType,
                    linkedBillIds,
                } = values;

                const emails = pipe(
                    values.emails,
                    array.map((e) => e?.value),
                    array.concat(values.defaultEmails),
                    compactArray,
                    array.uniq(string.Eq)
                );

                const isRepeatedPrint =
                    !!requirePrintReasonOnTemplateChange &&
                    !!firstUsedTemplateType &&
                    firstUsedTemplateType !== template;

                if (isRepeatedPrint) {
                    dispatch(openDialog(repeatedPrintForm));
                    return dispatch(
                        initialize(repeatedPrintForm, {
                            financialDocumentId,
                            financialDocumentType: documentType,
                            emails,
                            language,
                            printReason,
                            setShowPrintReason,
                            template,
                            firstUsedTemplateType,
                            linkedBillIds,
                            type,
                        })
                    );
                }

                switch (type) {
                    case 'SendBill':
                        return dispatch(
                            sendEmails({
                                financialDocumentId,
                                financialDocumentType: documentType,
                                emails,
                                language,
                                printReason,
                                setShowPrintReason,
                                template,
                                linkedBillIds,
                            })
                        );
                    case 'SendReminder':
                        return dispatch(
                            sendInvoiceReminder({
                                invoiceId: financialDocumentId,
                                emails,
                                language,
                                printReason,
                                setShowPrintReason,
                                template,
                                linkedBillIds,
                            })
                        );
                }
            },
            onSubmitSuccess: () => dispatch(closeDialog(type === 'SendBill' ? FORM_NAME : REMINDER_DIALOG_NAME)),
        })
    )
);
